export const SEED_DATA_XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const SEED_DATA_SERVER_SIDE_ENCRYPTION = 'AES256';

export type SeedDataUploadProgress = {
  loadedBytes: number;
  totalBytes: number;
};

export class SeedDataUploadAbortedError extends Error {
  constructor() {
    super('Seed data upload was cancelled.');
    this.name = SeedDataUploadAbortedError.name;
  }
}

export class SeedDataUploadTransportError extends Error {
  constructor() {
    super('The file could not be uploaded. Please try again.');
    this.name = SeedDataUploadTransportError.name;
  }
}

export type SeedDataXhrFactory = () => XMLHttpRequest;

export type SeedDataUploadTransportOptions = {
  file: File;
  uploadUrl: string;
  onProgress: (progress: SeedDataUploadProgress) => void;
  xhrFactory?: SeedDataXhrFactory;
};

export type SeedDataUploadTask = {
  promise: Promise<void>;
  abort: () => void;
};

const defaultXhrFactory: SeedDataXhrFactory = () => new XMLHttpRequest();

// XHR is intentional: browser File uploads need byte-level progress and a real
// abort handle. The presigned URL is only held in this function while the request
// is in flight and is never persisted, rendered, or included in an error.
export const uploadSeedDataXlsx = ({
  file,
  uploadUrl,
  onProgress,
  xhrFactory = defaultXhrFactory,
}: SeedDataUploadTransportOptions): SeedDataUploadTask => {
  const xhr = xhrFactory();
  let settled = false;
  let rejectPromise: (error: Error) => void = () => {};

  const clearHandlers = (): void => {
    xhr.upload.onprogress = null;
    xhr.onload = null;
    xhr.onerror = null;
    xhr.onabort = null;
    xhr.ontimeout = null;
  };

  const promise = new Promise<void>((resolve, reject) => {
    rejectPromise = reject;

    const resolveOnce = (): void => {
      if (settled) return;
      settled = true;
      clearHandlers();
      resolve();
    };

    const rejectOnce = (error: Error): void => {
      if (settled) return;
      settled = true;
      clearHandlers();
      reject(error);
    };

    xhr.upload.onprogress = event => {
      const loadedBytes = Math.max(0, Math.min(file.size, event.loaded));
      const totalBytes = event.lengthComputable && event.total > 0 ? Math.min(file.size, event.total) : file.size;
      onProgress({ loadedBytes, totalBytes });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolveOnce();
        return;
      }

      rejectOnce(new SeedDataUploadTransportError());
    };
    xhr.onerror = () => rejectOnce(new SeedDataUploadTransportError());
    xhr.ontimeout = () => rejectOnce(new SeedDataUploadTransportError());
    xhr.onabort = () => rejectOnce(new SeedDataUploadAbortedError());

    try {
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', SEED_DATA_XLSX_CONTENT_TYPE);
      xhr.setRequestHeader('x-amz-server-side-encryption', SEED_DATA_SERVER_SIDE_ENCRYPTION);
      xhr.send(file);
    } catch {
      rejectOnce(new SeedDataUploadTransportError());
    }
  });

  return {
    promise,
    abort: () => {
      if (settled) return;
      xhr.abort();
      if (!settled) {
        settled = true;
        clearHandlers();
        rejectPromise(new SeedDataUploadAbortedError());
      }
    },
  };
};
